import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = ["width", "height"]
const _hoisted_2 = ["width", "height"]
const _hoisted_3 = ["width", "height"]
const _hoisted_4 = ["onClick"]

import { computed, nextTick, onBeforeUnmount, onMounted, type PropType, ref, type Ref, toRefs, watch } from 'vue';

import type { PriceLevel, OrderbookMyOrderIndicator, OrderbookMyOrdersIndicators } from '@exchange/libs/orderbook/service/src';
import { findIndex, OrderbookSide } from '@exchange/libs/orderbook/service/src';
import { themingInstance } from '@exchange/libs/utils/theming/src';

import {
  type DisplayedValue,
  getAmountBarColor,
  getAppliedPrecisionString,
  getBackgroundColor,
  getCanvasHeight,
  getClickedPricelevelProp,
  getColumnTextAlignment,
  getColumnWidth,
  getItemHeight,
  getPaddingVertical,
  getY,
  LevelColumns,
  NUMBER_OF_PRICELEVELS,
  type OrderbookPriceLevelColumn,
  PADDING_HORIZONTAL,
  RENDERING_SCALE,
} from './OrderbookPricelevel';


export default /*#__PURE__*/_defineComponent({
  __name: 'OrderbookPricelevels',
  props: {
  amountPrecision: { type: Number, required: true },
  columns: { type: Array as PropType<Array<OrderbookPriceLevelColumn>>, required: true },
  marketPrecision: { type: Number, required: true },
  maxAmount: { type: Number, required: true },
  mini: { type: Boolean, required: true },
  msPerFrame: { type: Number, required: true },
  myOrderIndicators: { type: Object as PropType<OrderbookMyOrdersIndicators>, required: true },
  pricelevels: { type: Array as PropType<Array<PriceLevel>>, required: true },
  side: { type: String as PropType<OrderbookSide>, required: true },
  totalPrecision: { type: Number, required: true },
  width: { type: Number, required: true },
  withOrderIndicators: { type: Boolean, default: true },
},
  emits: ["item-clicked"],
  setup(__props, { emit: __emit }) {

const props = __props;

const emit = __emit;

const { myOrderIndicators, pricelevels, columns } = toRefs(props);

const canvasFG = ref<HTMLCanvasElement>();
const canvasBG = ref<HTMLCanvasElement>();
const canvasMOI = ref<HTMLCanvasElement>();

let contextFG: CanvasRenderingContext2D;
let contextBG: CanvasRenderingContext2D;
let contextMOI: CanvasRenderingContext2D;

const foregroundValues = Array<[DisplayedValue?, DisplayedValue?, DisplayedValue?]>();
const amountBarValue: Array<number> = [];
const ITEM_HEIGHT = getItemHeight();
const PADDING_VERTICAL = getPaddingVertical(ITEM_HEIGHT);
const CANVAS_HEIGHT = getCanvasHeight(ITEM_HEIGHT);

let stop = false;
let dirty = true;
let bgDirty = true;

const visiblePricelevelCount = computed(() => Math.min(pricelevels.value.length, NUMBER_OF_PRICELEVELS));
const isAsk = computed(() => props.side === OrderbookSide.ASK);
const canvasWidth = computed(() => (props.width ?? 0) * RENDERING_SCALE);

const backgroundColor = getBackgroundColor();

const onPricelevelClick = (index: number, event: MouseEvent) => {
  const { amountPrecision, marketPrecision, totalPrecision } = props;

  const key = getClickedPricelevelProp(event, props.width, props.mini);
  const pl = pricelevels.value[index - 1];

  if (!pl) {
    return;
  }

  emit('item-clicked', {
    key,
    amount: getAppliedPrecisionString(pl.amount, amountPrecision),
    price: getAppliedPrecisionString(pl.price, marketPrecision),
    total: getAppliedPrecisionString(pl.total, totalPrecision),
  });
};

const setContextFGStyle = () => {
  if (!contextFG) {
    return;
  }

  const fontSize = themingInstance.getFontSize('sm') * RENDERING_SCALE;
  const fontWeight = themingInstance.getFontWeight('medium');
  const fontFamily = themingInstance.appTheme.getCurrentThemeConfig().font['font-family'];

  contextFG.font = `${fontWeight} ${fontSize}px ${fontFamily}`;

  if (contextFG.textAlign !== 'right') {
    contextFG.textAlign = 'right';
  }
};

const getCanvasContext = (canvas: HTMLCanvasElement): CanvasRenderingContext2D => canvas.getContext('2d', { alpha: true }) as CanvasRenderingContext2D;

const stopWatchFG = watch(
  canvasFG,
  () => {
    if (canvasFG.value) {
      stopWatchFG();
      contextFG = getCanvasContext(canvasFG.value);
      setContextFGStyle();
    }
  },
  { immediate: true },
);

const stopWatchBG = watch(
  canvasBG,
  () => {
    if (canvasBG.value) {
      stopWatchBG();
      contextBG = getCanvasContext(canvasBG.value);
    }
  },
  { immediate: true },
);

const stopWatchMOI = watch(
  canvasMOI,
  () => {
    if (canvasMOI.value) {
      stopWatchMOI();
      contextMOI = getCanvasContext(canvasMOI.value);
    }
  },
  { immediate: true },
);

watch(
  () => props.width,
  () => {
    /** Watching width to know when to resize and redraw the canvas */
    // after changing a canvases with - it resets its context style so we need to set it again.
    nextTick().then(() => setContextFGStyle());
    dirty = true;
  },
  { immediate: true },
);

watch(
  () => [props.maxAmount, pricelevels.value.length],
  () => {
    /** Watching max amout & level numbers to know when to redraw amount bars */
    bgDirty = true;
  },
);

const drawForeground = (pl: PriceLevel, i: number, y: number, width: number) => {
  columns.value.forEach((c, j) => {
    const color = c.getColor(pl);

    // logger.log({
    //   TEXT: pl[c.prop] !== foregroundValues[i]?.[j]?.text,
    //   COLOR: color !== foregroundValues[i]?.[j]?.color,
    //   DIRTY: dirty,

    // }, pl[c.prop], foregroundValues[i]?.[j]?.text, c.prop);

    if (
      pl[c.prop] !== foregroundValues[i]?.[j]?.text || // text has changed
      color !== foregroundValues[i]?.[j]?.color || // color of text has changed
      dirty // item has was resized
    ) {
      let foregroundValuesI = foregroundValues[i];

      if (!foregroundValuesI) {
        foregroundValuesI = [];
        foregroundValues[i] = foregroundValuesI;
      }

      foregroundValuesI[j] = {
        text: pl[c.prop],
        color,
      };
      const { amountPrecision, marketPrecision, totalPrecision } = props;
      const text = c.value(pl, { amountPrecision, marketPrecision, totalPrecision });
      const columnWidth = getColumnWidth(width, columns.value.length);
      const rightEdge = PADDING_HORIZONTAL + (j + 1) * columnWidth;
      const leftEdge = PADDING_HORIZONTAL + j * columnWidth;

      // Delete what has been there
      if (j === 0) {
        // it is possible that numbers overflow out of the column into the padding - so for the first column we need to also clear the padding
        // so we just start at 0 (all the way left) and add the padding to the column width.
        contextFG.clearRect(0, y, columnWidth + PADDING_HORIZONTAL, ITEM_HEIGHT);
      } else {
        // all other columns are fine as they cannot overflow given the current markets and if they would we need to change the design as
        contextFG.clearRect(PADDING_HORIZONTAL + j * columnWidth, y, columnWidth, ITEM_HEIGHT);
      }

      const textAlign = getColumnTextAlignment({ column: c, mini: props.mini });
      const textX = textAlign === 'left' ? leftEdge : rightEdge;

      // render the new text
      contextFG.textAlign = textAlign;
      contextFG.fillStyle = color;
      contextFG.fillText(text, Math.round(textX), Math.round(y + PADDING_VERTICAL));
    }
  });
};

const drawMyOrderIndicator = (moi: OrderbookMyOrderIndicator, type: 'order' | 'trigger') => {
  const visiblePricelevelLastPrice = pricelevels.value[visiblePricelevelCount.value - 1]?.price;

  if (
    !moi.obSides.includes(props.side) ||
    (props.side === OrderbookSide.ASK && visiblePricelevelLastPrice !== undefined && moi.price > visiblePricelevelLastPrice) ||
    (props.side === OrderbookSide.BID && visiblePricelevelLastPrice !== undefined && moi.price < visiblePricelevelLastPrice)
  ) {
    return;
  }

  const { index, exists } = findIndex(moi.price, pricelevels.value, props.side);

  const y = getY(props.side, index, ITEM_HEIGHT, CANVAS_HEIGHT);
  const verticalCenter = y + ITEM_HEIGHT / 2;
  const beforeOffset = props.side === OrderbookSide.BID ? y : y + ITEM_HEIGHT;
  let x = 8;

  if (type === 'trigger') {
    x = 0;
  }

  contextMOI.fillStyle = themingInstance.getColor('--v-theme-primary');
  contextMOI.beginPath();
  contextMOI.arc(x, exists ? verticalCenter : beforeOffset, 6, 0, 2 * Math.PI);
  contextMOI.fill();
};
const drawMyOrderIndicators = (width: number) => {
  contextMOI.clearRect(0, 0, width, CANVAS_HEIGHT);

  myOrderIndicators.value.orders
    ?.filter((moi) => moi.obSides.indexOf(props.side) !== -1)
    .forEach((moi) => {
      drawMyOrderIndicator(moi, 'order');
    });

  myOrderIndicators.value.triggers
    ?.filter((moi) => moi.obSides.indexOf(props.side) !== -1)
    .forEach((moi) => {
      drawMyOrderIndicator(moi, 'trigger');
    });
};

const drawBackground = (pl: PriceLevel, index: number, y: number, width: number) => {
  const amountBarWidth = Math.round(width * ((isAsk.value ? pl.amount : pl.total) / props.maxAmount));

  const miniOt = props.mini;

  const miniHeightMultiplier = 0.7;
  const itemHeightToUse = miniOt ? Math.round(ITEM_HEIGHT * miniHeightMultiplier) : ITEM_HEIGHT;
  const rectY = miniOt ? Math.round(y + ITEM_HEIGHT * ((1 - miniHeightMultiplier) / 2)) : y;

  if (dirty || bgDirty || amountBarWidth !== amountBarValue[index]) {
    amountBarValue[index] = amountBarWidth;

    contextBG.clearRect(0, y, width, ITEM_HEIGHT);
    contextBG.fillStyle = getAmountBarColor(pl);

    if (miniOt) {
      contextBG.strokeStyle = contextBG.fillStyle;
      contextBG.beginPath();
      contextBG.roundRect(width - amountBarWidth, rectY, amountBarWidth, itemHeightToUse, [4, 0, 0, 4]);
      contextBG.stroke();
      contextBG.fill();
    } else {
      contextBG.fillRect(width - amountBarWidth, rectY, amountBarWidth, itemHeightToUse);
    }
  }

  contextBG.fillStyle = backgroundColor;
  contextBG.fillRect(0, rectY, width - amountBarWidth, itemHeightToUse);
};

let previousDrawnLevels = 0;
const clearUnusedCanvas = () => {
  if (!contextFG || !contextBG || !contextMOI) {
    return;
  }

  // clear the displayedValues;
  let i = 0;

  while (pricelevels.value.length + i < 50) {
    delete foregroundValues[pricelevels.value.length + i];
    i += 1;
  }

  // clear the canvas
  if (pricelevels.value.length !== previousDrawnLevels && pricelevels.value.length < NUMBER_OF_PRICELEVELS) {
    const y = props.side === OrderbookSide.ASK ? 0 : getY(props.side, pricelevels.value.length, ITEM_HEIGHT, CANVAS_HEIGHT);
    const h = props.side === OrderbookSide.ASK ? getY(props.side, pricelevels.value.length - 1, ITEM_HEIGHT, CANVAS_HEIGHT) : CANVAS_HEIGHT - y;

    contextFG.clearRect(0, y, canvasWidth.value, h);
    contextBG.clearRect(0, y, canvasWidth.value, h);
    contextMOI.clearRect(0, y, canvasWidth.value, h);

    previousDrawnLevels = pricelevels.value.length;
  }
};

const draw = () => {
  if (!contextFG || !contextBG || !contextMOI) {
    return;
  }

  pricelevels.value.forEach((pl, i) => {
    const y = getY(pl.side, i, ITEM_HEIGHT, CANVAS_HEIGHT);
    const { width } = props;

    if (width) {
      const updatedWith = width * RENDERING_SCALE;

      drawForeground(pl, i, y, updatedWith);
      drawBackground(pl, i, y, updatedWith);

      if (props.withOrderIndicators) {
        drawMyOrderIndicators(updatedWith);
      }
    }
  });

  clearUnusedCanvas();

  dirty = false;
  bgDirty = false;
};

let lastDrawTimestamp = 0;

const drawLoop = () => {
  if (stop) return;

  requestAnimationFrame((now: DOMHighResTimeStamp) => {
    if (now - lastDrawTimestamp > props.msPerFrame) {
      draw();
      lastDrawTimestamp = now;
    }

    drawLoop();
  });
};

/**
 * Some browsers retain canvas elements for a short period, while also limiting the total amount of memory usable by canvas elements.
 * This behavior cannot be prevented, but this method ensures that cached canvas elements require the least memory possible.
 *
 * @param canvasRef the reference to the canvas element to clear
 */
const clearCanvas = (canvasRef: Ref<HTMLCanvasElement | undefined>): void => {
  const canvas = canvasRef.value;

  if (canvas === undefined) {
    return;
  }

  canvas.width = 1;
  canvas.height = 1;

  const ctx = getCanvasContext(canvas);

  if (ctx) {
    ctx.clearRect(0, 0, 1, 1);
  }
};

onMounted(() => {
  drawLoop();
});

onBeforeUnmount(() => {
  stop = true;
  clearCanvas(canvasFG);
  clearCanvas(canvasBG);
  clearCanvas(canvasMOI);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["orderbook-side", { 'orderbook-side--ask': isAsk.value }])
  }, [
    _createElementVNode("canvas", {
      ref_key: "canvasBG",
      ref: canvasBG,
      class: "orderbook-side__canvas-bg",
      width: canvasWidth.value,
      height: _unref(CANVAS_HEIGHT)
    }, null, 8, _hoisted_1),
    _createElementVNode("canvas", {
      ref_key: "canvasFG",
      ref: canvasFG,
      class: "orderbook-side__canvas-fg",
      width: canvasWidth.value,
      height: _unref(CANVAS_HEIGHT)
    }, null, 8, _hoisted_2),
    _createElementVNode("canvas", {
      ref_key: "canvasMOI",
      ref: canvasMOI,
      class: "orderbook-side__canvas-moi",
      width: canvasWidth.value,
      height: _unref(CANVAS_HEIGHT)
    }, null, 8, _hoisted_3),
    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visiblePricelevelCount.value, (n) => {
      return (_openBlock(), _createElementBlock("div", {
        key: n,
        class: "orderbook-side__level",
        onClick: (event) => onPricelevelClick(n, event)
      }, null, 8, _hoisted_4))
    }), 128))
  ], 2))
}
}

})